// Native In-App Purchase service for iOS (StoreKit) and Android (Google Play Billing)
// Uses @capgo/native-purchases under the hood.
//
// Product setup required BEFORE this works:
//   - App Store Connect -> In-App Purchases -> Non-Consumable:
//       Product ID: com.foresighttrackfield.premium
//       Reference Name: Premium Unlock
//       Price Tier: Tier 3 ($2.99)
//   - Google Play Console -> Monetize -> Products -> In-app products:
//       Product ID: com.foresighttrackfield.premium
//       Type: One-time (managed)
//       Price: $2.99 (or local equivalent)
//
// Both stores must have the product ID match EXACTLY: com.foresighttrackfield.premium
import { Capacitor } from '@capacitor/core';
import { NativePurchases, PURCHASE_TYPE } from '@capgo/native-purchases';
import { PRODUCT_IDS } from './purchases';

const PREMIUM_PRODUCT_ID = PRODUCT_IDS.PREMIUM_UNLOCK;

export const isNativePurchaseSupported = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Fetch live product info (price, title) from the platform store.
 * Returns { identifier, price, priceString, title, description, currency } or null.
 */
export const getPremiumProduct = async () => {
  if (!isNativePurchaseSupported()) return null;
  try {
    const { products } = await NativePurchases.getProducts({
      productIdentifiers: [PREMIUM_PRODUCT_ID],
      type: PURCHASE_TYPE.NON_CONSUMABLE,
    });
    return (products && products[0]) || null;
  } catch (err) {
    console.error('[IAP] getPremiumProduct failed:', err);
    return null;
  }
};

/**
 * Launch the native purchase flow for the premium unlock.
 * Resolves with { transactionId } on success, throws on cancel/failure.
 */
export const purchasePremium = async () => {
  if (!isNativePurchaseSupported()) {
    throw new Error('Native purchases are only available on iOS and Android.');
  }
  const result = await NativePurchases.purchaseProduct({
    productIdentifier: PREMIUM_PRODUCT_ID,
    productType: PURCHASE_TYPE.NON_CONSUMABLE,
  });
  const transactionId =
    result?.transactionId ||
    result?.purchase?.transactionId ||
    result?.originalTransactionId ||
    `native-${Date.now()}`;
  return { transactionId, raw: result };
};

/**
 * Restore previously-owned premium purchase (required by App Store review guidelines).
 * Resolves with { restored: boolean, transactionId?: string }.
 */
export const restorePremium = async () => {
  if (!isNativePurchaseSupported()) {
    return { restored: false };
  }
  try {
    const result = await NativePurchases.restorePurchases();
    const purchases = result?.purchases || result?.restoredPurchases || [];
    const match = purchases.find(
      (p) => (p.productIdentifier || p.productId) === PREMIUM_PRODUCT_ID,
    );
    if (match) {
      return {
        restored: true,
        transactionId:
          match.transactionId || match.originalTransactionId || `restored-${Date.now()}`,
      };
    }
    return { restored: false };
  } catch (err) {
    console.error('[IAP] restorePremium failed:', err);
    throw err;
  }
};
